import React from 'react';
import { Box, ButtonGroup, Button, Spinner, useColorMode, useColorModeValue } from '@chakra-ui/react';
import { CandleInterval } from '../services/upstoxData';
// Import ApexCharts directly since we're using Vite
import ReactApexChart from 'react-apexcharts';

interface CandleChartProps {
  data: {
    timestamp: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }[];
  isLoading?: boolean;
  onIntervalChange?: (interval: CandleInterval) => void;
  selectedInterval?: CandleInterval;
}

export const CandleChart: React.FC<CandleChartProps> = ({
  data,
  isLoading,
  onIntervalChange,
  selectedInterval = 'day'
}) => {
  const { colorMode } = useColorMode();
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const gridColor = useColorModeValue('gray.200', 'gray.700');
  const tooltipTheme = colorMode === 'dark' ? 'dark' : 'light';

  const options = {
    chart: {
      type: 'candlestick' as const,
      height: 400,
      width: '100%',
      background: 'transparent',
      foreColor: textColor,
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      animations: {
        enabled: false, // Disable animations on mobile for better performance
      },
      redrawOnParentResize: true,
    },
    theme: {
      mode: colorMode as 'light' | 'dark',
    },
    tooltip: {
      theme: tooltipTheme,
      style: {
        fontSize: '12px',
        fontFamily: 'inherit',
      },
    },
    xaxis: {
      type: 'datetime' as const,
      labels: {
        style: {
          colors: textColor,
        },
      },
      axisBorder: {
        color: gridColor,
      },
      axisTicks: {
        color: gridColor,
      },
    },
    yaxis: {
      tooltip: {
        enabled: true,
      },
      labels: {
        style: {
          colors: textColor,
        },
        formatter: (value: number) => value.toFixed(2),
      },
    },
    grid: {
      borderColor: gridColor,
      xaxis: {
        lines: {
          show: true,
          colors: gridColor,
        },
      },
      yaxis: {
        lines: {
          show: true,
          colors: gridColor,
        },
      },
    },
    plotOptions: {
      candlestick: {
        colors: {
          upward: '#48BB78',
          downward: '#F56565',
        },
        wick: {
          useFillColor: true,
        },
      },
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          height: 300
        },
        xaxis: {
          labels: {
            rotate: -45,
            style: {
              fontSize: '10px'
            }
          }
        }
      }
    }]
  };

  const series = [{
    data: data.map(candle => ({
      x: new Date(candle.timestamp).getTime(),
      y: [candle.open, candle.high, candle.low, candle.close]
    }))
  }];

  const intervals: CandleInterval[] = ['1minute', '30minute', 'day', 'week', 'month'];

  if (isLoading) {
    return (
      <Box height="400px" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box>
      {onIntervalChange && (
        <ButtonGroup 
          size={{ base: "xs", md: "sm" }} 
          isAttached 
          mb={4}
          overflowX="auto"
          w="full"
        >
          {intervals.map(interval => (
            <Button
              key={interval}
              onClick={() => onIntervalChange(interval)}
              colorScheme={selectedInterval === interval ? 'blue' : 'gray'}
              variant={selectedInterval === interval ? 'solid' : 'outline'}
            >
              {interval.charAt(0).toUpperCase() + interval.slice(1)}
            </Button>
          ))}
        </ButtonGroup>
      )}
      <Box
        borderRadius="lg"
        overflow="hidden"
        w="100%"
        _hover={{
          '.apexcharts-tooltip': {
            color: textColor,
          },
        }}
      >
        <ReactApexChart
          options={options}
          series={series}
          type="candlestick"
          height={400}
        />
      </Box>
    </Box>
  );
};
